/**
 * RemindersDialog — popup listing the user's calendar reminders with
 * Active / Completed toggle. Each row shows date · time · title · color,
 * creator, assignee, and lets the user toggle complete state and reassign
 * inline. Reminders are company-shared; toggle "Mine" to scope to assignee=me.
 */
import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, CheckCircle2, Circle, ArrowUpRight, AlertCircle, UserCog } from "lucide-react";

const COLOR_BORDER = {
    grey: "border-l-slate-400", blue: "border-l-ikb", amber: "border-l-amber-500",
    rose: "border-l-rose-500", emerald: "border-l-emerald-500",
};

function pillForDate(dateStr) {
    if (!dateStr) return { text: "No date", cls: "bg-secondary text-foreground/70" };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const [y, m, d] = dateStr.split("-").map(Number);
    if (!y || !m || !d) return { text: dateStr, cls: "bg-secondary text-foreground/70" };
    const dt = new Date(y, m - 1, d);
    const diff = Math.round((dt - today) / 86400000);
    if (diff < 0) return { text: `Overdue · ${Math.abs(diff)}d ago`, cls: "bg-rose-50 text-rose-700 border border-rose-200" };
    if (diff === 0) return { text: "Today", cls: "bg-amber-50 text-amber-700 border border-amber-200" };
    if (diff <= 7) return { text: `In ${diff}d`, cls: "bg-blue-50 text-ikb border border-blue-200" };
    return { text: dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }), cls: "bg-secondary text-foreground/70" };
}

export default function RemindersDialog({ open, onOpenChange, onOpen, refreshKey = 0, currentUserId }) {
    const [tab, setTab] = useState("active");
    const [scope, setScope] = useState("all"); // "all" | "mine"
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ counts: { active: 0, overdue: 0, today: 0, upcoming: 0, completed: 0 }, active: [], completed: [] });
    const [assignableUsers, setAssignableUsers] = useState([]);
    const [reassignOpenFor, setReassignOpenFor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = scope === "mine" ? { assigned_to_me: true } : {};
            const { data } = await api.get("/calendar/reminders/summary", { params });
            setSummary(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, refreshKey, scope]);
    useEffect(() => {
        if (!open) return;
        api.get("/calendar/assignable-users").then(({ data }) => setAssignableUsers(data?.items || [])).catch(() => {});
    }, [open]);

    const toggle = async (note) => {
        try {
            await api.post(`/calendar/notes/${note.id}/toggle-complete`);
            toast.success(note.completed ? "Marked active" : "Marked complete");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const reassign = async (note, userId) => {
        try {
            await api.post(`/calendar/notes/${note.id}/reassign`, { assigned_to: userId });
            toast.success("Reassigned");
            setReassignOpenFor(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const list = tab === "active" ? summary.active : summary.completed;
    const grouped = useMemo(() => {
        if (tab !== "active") return null;
        const buckets = { overdue: [], today: [], upcoming: [], noDate: [] };
        const today = new Date(); today.setHours(0, 0, 0, 0);
        for (const n of list) {
            const d = n.date;
            if (!d) { buckets.noDate.push(n); continue; }
            const [y, m, dd] = d.split("-").map(Number);
            const nd = new Date(y, m - 1, dd);
            if (nd < today) buckets.overdue.push(n);
            else if (nd.getTime() === today.getTime()) buckets.today.push(n);
            else buckets.upcoming.push(n);
        }
        return buckets;
    }, [list, tab]);

    const Row = ({ n }) => {
        const pill = pillForDate(n.date);
        const border = COLOR_BORDER[n.color || "blue"] || COLOR_BORDER.blue;
        const isMine = n.assigned_to === currentUserId;
        return (
            <div className={`group border ${border} border-l-4 bg-white p-3 hover:bg-secondary/40 flex items-start gap-3 relative`} data-testid={`rem-row-${n.id}`}>
                <button
                    onClick={() => toggle(n)}
                    className="mt-0.5 shrink-0"
                    title={n.completed ? "Mark active" : "Mark complete"}
                    data-testid={`rem-toggle-${n.id}`}
                >
                    {n.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-foreground/40 hover:text-ikb" />}
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 ${pill.cls}`}>{pill.text}</span>
                        {n.time && <span className="text-[11px] font-mono text-foreground/70">{n.time}</span>}
                        {!n.time && <span className="text-[10px] font-mono text-foreground/50 uppercase">All-day</span>}
                        {isMine && <span className="text-[9px] font-mono uppercase tracking-widest bg-ikb text-white px-1 py-0.5">Mine</span>}
                    </div>
                    <div className={`text-sm font-medium mt-1 ${n.completed ? "line-through text-foreground/50" : ""}`}>{n.title}</div>
                    {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] font-mono text-foreground/60 mt-1.5 flex items-center gap-2 flex-wrap">
                        <span>By <b className="text-foreground/80">{n.created_by_name || "—"}</b></span>
                        <span>·</span>
                        <span>Assigned to <b className="text-foreground/80">{n.assigned_to_name || n.created_by_name || "—"}</b></span>
                        <button
                            type="button"
                            onClick={() => setReassignOpenFor(reassignOpenFor === n.id ? null : n.id)}
                            className="inline-flex items-center gap-1 text-ikb hover:underline"
                            data-testid={`rem-reassign-btn-${n.id}`}
                        >
                            <UserCog className="w-3 h-3" /> Reassign
                        </button>
                    </div>
                    {reassignOpenFor === n.id && (
                        <div className="mt-2 border border-border bg-white p-2 space-y-1" data-testid={`rem-reassign-panel-${n.id}`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Reassign to</div>
                            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                                {assignableUsers.length === 0 && <div className="text-[11px] text-muted-foreground col-span-2 py-1">No assignable users</div>}
                                {assignableUsers.map((u) => (
                                    <button
                                        key={u.id}
                                        onClick={() => reassign(n, u.id)}
                                        className={`text-left text-xs px-2 py-1 hover:bg-secondary ${u.id === n.assigned_to ? "bg-blue-50 font-semibold" : ""}`}
                                        data-testid={`rem-reassign-pick-${n.id}-${u.id}`}
                                    >
                                        <div className="truncate">{u.name || u.email}</div>
                                        <div className="text-[9px] font-mono text-foreground/50 uppercase">{u.role}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button
                    onClick={() => { onOpen?.(n); onOpenChange?.(false); }}
                    className="shrink-0 text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 hover:bg-secondary opacity-0 group-hover:opacity-100"
                    title="Open"
                    data-testid={`rem-open-${n.id}`}
                >
                    <ArrowUpRight className="w-3 h-3" />
                </button>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl rounded-none" data-testid="reminders-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Bell className="w-4 h-4 text-ikb" /> Reminders <span className="text-xs font-mono uppercase tracking-widest text-foreground/50 ml-2">Shared with team</span></DialogTitle>
                    <DialogDescription>Toggle complete inline, reassign to a teammate, or open a reminder to edit.</DialogDescription>
                </DialogHeader>

                {/* Scope toggle */}
                <div className="inline-flex border border-border bg-white w-fit" data-testid="rem-scope-toggle">
                    <button onClick={() => setScope("all")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${scope === "all" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="rem-scope-all">Everyone</button>
                    <button onClick={() => setScope("mine")} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-border ${scope === "mine" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="rem-scope-mine">Mine</button>
                </div>

                {/* Counts */}
                <div className="grid grid-cols-4 gap-px bg-border border border-border">
                    <div className="bg-white p-2 text-center">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Overdue</div>
                        <div className="font-display font-black text-xl text-rose-700">{summary.counts.overdue}</div>
                    </div>
                    <div className="bg-white p-2 text-center">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Today</div>
                        <div className="font-display font-black text-xl text-amber-700">{summary.counts.today}</div>
                    </div>
                    <div className="bg-white p-2 text-center">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Upcoming</div>
                        <div className="font-display font-black text-xl text-ikb">{summary.counts.upcoming}</div>
                    </div>
                    <div className="bg-white p-2 text-center">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Completed</div>
                        <div className="font-display font-black text-xl text-emerald-700">{summary.counts.completed}</div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="inline-flex border border-border bg-white" data-testid="rem-tabs">
                    <button
                        onClick={() => setTab("active")}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${tab === "active" ? "bg-ikb text-white" : "hover:bg-secondary"}`}
                        data-testid="rem-tab-active"
                    >
                        Active ({summary.counts.active})
                    </button>
                    <button
                        onClick={() => setTab("completed")}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-border ${tab === "completed" ? "bg-ikb text-white" : "hover:bg-secondary"}`}
                        data-testid="rem-tab-completed"
                    >
                        Completed ({summary.counts.completed})
                    </button>
                </div>

                <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1" data-testid="rem-list">
                    {loading && <div className="text-center text-xs font-mono text-muted-foreground py-6">Loading…</div>}
                    {!loading && list.length === 0 && (
                        <div className="text-center text-xs font-mono text-muted-foreground py-8 border border-dashed border-border">
                            {tab === "active" ? "Nothing on your plate — go enjoy a coffee." : "No completed reminders yet."}
                        </div>
                    )}
                    {tab === "active" && grouped && (
                        <>
                            {grouped.overdue.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700 mt-2 mb-1.5 flex items-center gap-1.5">
                                        <AlertCircle className="w-3 h-3" /> Overdue ({grouped.overdue.length})
                                    </div>
                                    <div className="space-y-1.5">{grouped.overdue.map((n) => <Row key={n.id} n={n} />)}</div>
                                </div>
                            )}
                            {grouped.today.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mt-3 mb-1.5">Today ({grouped.today.length})</div>
                                    <div className="space-y-1.5">{grouped.today.map((n) => <Row key={n.id} n={n} />)}</div>
                                </div>
                            )}
                            {grouped.upcoming.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb mt-3 mb-1.5">Upcoming ({grouped.upcoming.length})</div>
                                    <div className="space-y-1.5">{grouped.upcoming.map((n) => <Row key={n.id} n={n} />)}</div>
                                </div>
                            )}
                            {grouped.noDate.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mt-3 mb-1.5">No date</div>
                                    <div className="space-y-1.5">{grouped.noDate.map((n) => <Row key={n.id} n={n} />)}</div>
                                </div>
                            )}
                        </>
                    )}
                    {tab === "completed" && (
                        <div className="space-y-1.5">{list.map((n) => <Row key={n.id} n={n} />)}</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

